// 🧪 Archivo de prueba para el sistema de logging
// Ejecutar desde la consola del navegador para probar el logger

import logger from './logger';

// Función para probar diferentes tipos de logs
export const testLogger = async () => {
    console.log('🧪 Iniciando pruebas del sistema de logging...');

    // Test 1: Log básico
    await logger.info('Prueba de log básico');

    // Test 2: Log con datos extras
    await logger.warn('Prueba de warning', {
        tipo: 'TEST_WARNING',
        datos: { test: true, numero: 123 }
    });

    // Test 3: Error con stack trace
    try {
        throw new Error('Error de prueba');
    } catch (err) {
        await logger.error('Error capturado en test', {
            tipo: 'TEST_ERROR',
            stack: err.stack,
            datos: { testMode: true }
        });
    }

    // Test 4: Error de GPS simulado
    await logger.gpsError('GPS no disponible en test', {
        code: 1,
        message: 'User denied geolocation'
    });

    // Test 5: Error de fichaje simulado
    await logger.fichajeError('test_user', 'entrada', new Error('Red no disponible'));

    // Test 6: Error de red simulado
    await logger.networkError('/api/test', new Error('Network timeout'));

    console.log('✅ Pruebas completadas. Revisa los logs en el servidor.');
};

// Función para ver logs recientes
export const verLogsRecientes = async () => {
    try {
        const API_URL = process.env.REACT_APP_API_URL || '';
        const response = await fetch(`${API_URL}/api/logs-frontend?limite=20`);
        const data = await response.json();

        console.log('📝 Logs recientes:', data);
        return data;
    } catch (err) {
        console.error('❌ Error al obtener logs:', err);
    }
};

// 🔧 NUEVO: Función para verificar estado del logger
export const verificarLogger = () => {
    const stats = logger.getStats();
    console.log('📊 Estadísticas del Logger:', stats);

    if (stats.queueLength > 0) {
        console.warn(`⚠️ Hay ${stats.queueLength} elementos en cola`);
    }

    if (stats.sending) {
        console.log('📤 Logger está enviando datos...');
    } else {
        console.log('✅ Logger en reposo');
    }

    return stats;
};

// 🧹 NUEVO: Función para limpiar el logger
export const limpiarLogger = () => {
    logger.cleanup();
    return verificarLogger();
};

// 🚨 NUEVO: Función de emergencia para detener logs
export const pararLogs = () => {
    console.log('🚨 ACTIVANDO MODO EMERGENCIA - PARANDO TODOS LOS LOGS');

    // Sobrescribir funciones de logger
    logger.log = () => console.log('🚫 Log bloqueado por modo emergencia');
    logger.error = () => console.log('🚫 Error log bloqueado');
    logger.warn = () => console.log('🚫 Warn log bloqueado');
    logger.info = () => console.log('🚫 Info log bloqueado');
    logger.debug = () => console.log('🚫 Debug log bloqueado');

    logger.cleanup();
    console.log('✅ Todos los logs han sido detenidos');
};

// 🔍 NUEVO: Verificar estado de la base de datos
export const verificarBaseDatos = async () => {
    try {
        const API_URL = process.env.REACT_APP_API_URL || '';
        const response = await fetch(`${API_URL}/api/db-schema-check`);
        const data = await response.json();

        console.log('🔍 Estado de la Base de Datos:', data);

        if (data.success && data.columnInfo) {
            const { type, maxLength, isAdequate, recommendation } = data.columnInfo;

            console.log(`📊 Columna 'mensaje': ${type}`);
            console.log(`📏 Tamaño máximo: ${maxLength} caracteres`);

            if (isAdequate) {
                console.log('✅ Base de datos configurada correctamente');
            } else {
                console.warn('⚠️ PROBLEMA DETECTADO:');
                console.warn(`   ${recommendation}`);
                console.warn('   📝 Ejecutar el archivo fix_mensaje_column.sql');
            }
        } else {
            console.error('❌ Error verificando BD:', data.error || 'Error desconocido');
        }

        return data;
    } catch (err) {
        console.error('❌ Error conectando con BD:', err);
        return null;
    }
};

// 📊 NUEVO: Monitor en tiempo real
export const monitorearLogger = (intervalos = 10) => {
    console.log(`🔍 Iniciando monitoreo por ${intervalos} intervalos de 5 segundos...`);

    let count = 0;
    const interval = setInterval(() => {
        count++;
        const stats = verificarLogger();

        if (stats.rateLimiting?.rateLimitActive) {
            console.warn('🚨 RATE LIMIT ACTIVO - Sistema protegido');
        }

        if (stats.queueLength > 5) {
            console.warn(`⚠️ Cola alta: ${stats.queueLength} elementos`);
        }

        if (count >= intervalos) {
            clearInterval(interval);
            console.log('✅ Monitoreo completado');
        }
    }, 5000);

    return interval;
};

// Exponer funciones globalmente para testing desde consola
if (typeof window !== 'undefined') {
    window.testLogger = testLogger;
    window.verLogsRecientes = verLogsRecientes;
    window.verificarLogger = verificarLogger;
    window.verificarBaseDatos = verificarBaseDatos; // 🔍 VERIFICAR BD
    window.limpiarLogger = limpiarLogger;
    window.pararLogs = pararLogs; // 🚨 EMERGENCIA
    window.monitorearLogger = monitorearLogger; // 📊 MONITOREO
    window.logger = logger; // Para usar directamente en consola
}

// Instrucciones de uso
console.log(`
🧪 SISTEMA DE LOGGING SUPER OPTIMIZADO CON RATE LIMITING

🔧 Funciones Básicas:
1. testLogger() - Ejecutar pruebas completas
2. verLogsRecientes() - Ver logs recientes  
3. verificarLogger() - Ver estado del sistema
4. verificarBaseDatos() - 🔍 VERIFICAR ESTRUCTURA DE BD
5. limpiarLogger() - Limpiar cola y resetear

🚨 Funciones de Emergencia:
6. pararLogs() - PARAR TODOS LOS LOGS INMEDIATAMENTE
7. monitorearLogger(10) - Monitorear por 10 intervalos

📊 Monitoreo Avanzado:
- logger.getStats() - Estadísticas completas incluyendo rate limiting
- Rate Limit: Máximo 20 logs por minuto
- Throttling automático por tipo de mensaje
- Debug: máximo cada 30s | Info: máximo cada 10s | Error: máximo cada 5s

🎯 Tipos de logs (con throttling automático):
- logger.error(mensaje, extras) [Max cada 5s]
- logger.warn(mensaje, extras) [Max cada 5s]
- logger.info(mensaje, extras) [Max cada 10s]
- logger.debug(mensaje, extras) [Max cada 30s]
- logger.gpsError(mensaje, error)
- logger.fichajeError(usuario, tipo, error)
- logger.networkError(url, error, response)

✅ PROTECCIONES ACTIVAS:
- Rate limiting global (20 logs/minuto)
- Throttling por tipo de mensaje
- useEffect optimizados en Dashboard
- Circuit breaker en backend
- Truncación automática de mensajes

✅ COLUMNA BD ACTUALIZADA A TEXT:
1. verificarBaseDatos() - Confirmar configuración
2. Logs ahora pueden usar hasta 1000 caracteres
3. Información completa de debugging disponible

🚨 Si hay problemas urgentes: pararLogs()
`);
